// Sermons.js
// -----------------------------------------------------------------------------
// "Watch & listen" experience for NHV: a hero with a YouTube CTA, the most
// recent sermon embedded, and a responsive grid of recent sermon cards.
//
// Data source is PRESERVED exactly: an axios GET to the existing proxy endpoint
// returns the channel's YouTube Atom feed XML, which is parsed into <entry>
// elements. Each card receives that entry's childNodes (unchanged contract).
// -----------------------------------------------------------------------------

import './styles/Sermons.css';
import axios from 'axios';
import { useEffect, useState } from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import Typography from '@mui/joy/Typography';
import AspectRatio from '@mui/joy/AspectRatio';
import YouTubeIcon from '@mui/icons-material/YouTube';

import VideoCard from '../components/VideoCard';
import {
  Section,
  SectionHeading,
  PageHero,
  PrimaryButton,
} from '../components/ui';
import usePageMeta from '../hooks/usePageMeta';

// Global constants: external endpoints and links. CAPITALIZED_SNAKE_CASE per
// the naming contract; these are deliberately module-level shared config.
const YOUTUBE_FEED_URL = 'https://v1-izlbep32qq-uc.a.run.app/youtube';
const YOUTUBE_CHANNEL_URL =
  'https://www.youtube.com/@newhorizonvineyardchurch4635';

// Parse an XML string and return all elements matching a tag name. Fails safe
// to an empty array; throws only on programmer error (non-string args).
function getXmlElementsByTag(xmlString, tagName) {
  if (typeof xmlString !== 'string') {
    throw new TypeError('xmlString must be a string.');
  }
  if (typeof tagName !== 'string') {
    throw new TypeError('tagName must be a string.');
  }

  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      console.error('XML Parsing Error:', parserError.textContent);
      return [];
    }
    const elements = xmlDoc.getElementsByTagName(tagName);
    return [...elements]; // Empty array if nothing matches.
  } catch (error) {
    console.error('Error parsing XML:', error);
    return [];
  }
}

const Sermons = () => {
  usePageMeta(
    'Sermons',
    'Watch the latest Sunday message and recent sermons from New Horizon Vineyard ' +
      'in Rowlett, TX.',
  );
  const [feed, setFeed] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    axios
      .get(YOUTUBE_FEED_URL)
      .then((response) => {
        const videos = getXmlElementsByTag(response.data, 'entry');
        setFeed(videos);
        setLoaded(true);
      })
      .catch((error) => {
        // Without this the page would show "Loading…" forever.
        console.error(error);
        setFailed(true);
        setLoaded(true);
      });
  }, []);

  // Featured (most recent) video id: same read as before, guarded.
  const featuredId = feed[0]?.childNodes[3]?.textContent;
  const hasFeatured = loaded && typeof featuredId === 'string' && featuredId.length > 0;
  const recentSermons = feed.slice(0, 6);

  return (
    <Box className="App-header">
      <PageHero
        eyebrow="Sermons"
        title="Watch & listen"
        subtitle="We’re always grateful to see you in person. Your presence brings life to our gatherings. Traveling or away? Our streams let you stay connected and revisit any service, anytime."
        height="sm"
        overlay="strong"
      >
        <PrimaryButton
          href={YOUTUBE_CHANNEL_URL}
          color="accent"
          startDecorator={<YouTubeIcon />}
        >
          Watch on YouTube
        </PrimaryButton>
      </PageHero>

      {/* Most recent sermon, embedded. */}
      <Section bg="default" padding="lg">
        <SectionHeading
          eyebrow="This week"
          title="Most recent sermon"
          subtitle="Catch the latest message from New Horizon Vineyard."
          align="center"
        />
        <Box sx={{ maxWidth: '900px', mx: 'auto', mt: { xs: 3, md: 5 } }}>
          {hasFeatured ? (
            <AspectRatio
              ratio="16/9"
              sx={{ borderRadius: 'lg', boxShadow: 'md', overflow: 'hidden' }}
            >
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${featuredId}?rel=0`}
                title="Most recent sermon"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                sx={{ border: 0, width: '100%', height: '100%' }}
              />
            </AspectRatio>
          ) : (
            <Stack
              spacing={1}
              sx={{
                alignItems: 'center',
                py: 8,
                borderRadius: 'lg',
                bgcolor: 'background.level1',
                color: 'text.secondary',
              }}
            >
              <Typography level="title-md" sx={{ color: 'text.secondary', textAlign: 'center', px: 2 }}>
                {failed
                  ? "We couldn't load the latest sermon right now."
                  : loaded
                    ? 'No sermon is available right now.'
                    : 'Loading the latest sermon…'}
              </Typography>
              {failed ? (
                <PrimaryButton
                  href={YOUTUBE_CHANNEL_URL}
                  color="accent"
                  startDecorator={<YouTubeIcon />}
                  sx={{ mt: 1.5 }}
                >
                  Watch on YouTube
                </PrimaryButton>
              ) : null}
            </Stack>
          )}
        </Box>
      </Section>

      {/* Recent sermons grid. */}
      <Section bg="tint" padding="lg">
        <SectionHeading
          eyebrow="Archive"
          title="Recent sermons"
          subtitle="Browse our latest messages and watch on YouTube."
          align="center"
        />
        <Box sx={{ mt: { xs: 3, md: 5 } }}>
          {loaded && recentSermons.length > 0 ? (
            <Grid container spacing={3} sx={{ flexGrow: 1 }}>
              {recentSermons.map((video, index) => {
                const id = video?.childNodes?.[3]?.textContent || index;
                return (
                  <Grid key={id} xs={12} sm={6} md={4}>
                    <VideoCard info={video.childNodes} />
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography
              level="body-lg"
              sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}
            >
              {failed
                ? 'Recent sermons are unavailable right now. You can find every message on our YouTube channel.'
                : loaded
                  ? 'No sermons were found.'
                  : 'Loading recent sermons…'}
            </Typography>
          )}
        </Box>
      </Section>

      {/* Closing CTA back to the channel. */}
      <Section bg="brand" padding="lg">
        <Stack spacing={2.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <SectionHeading
            eyebrow="Looking for more?"
            title="Watch every past stream"
            subtitle="All of our past live streams live on our YouTube channel."
            align="center"
            inverted
          />
          <PrimaryButton
            href={YOUTUBE_CHANNEL_URL}
            variant="solid"
            color="accent"
            startDecorator={<YouTubeIcon />}
          >
            Visit our YouTube channel
          </PrimaryButton>
        </Stack>
      </Section>
    </Box>
  );
};

export default Sermons;
